/** Symbols and loose spellings a model reads off an invoice, mapped to ISO 4217. */
const CURRENCY_ALIASES: Record<string, string> = {
  '₹': 'INR', 'RS': 'INR', 'RS.': 'INR', 'INR.': 'INR', 'RUPEES': 'INR',
  '$': 'USD', 'US$': 'USD', 'USD.': 'USD',
  '€': 'EUR', '£': 'GBP', '¥': 'JPY', 'AED.': 'AED',
}

export function normalizeCurrency(raw: string | null | undefined): string | null {
  const text = (raw ?? '').trim()
  if (!text) return null
  const upper = text.toUpperCase()
  if (CURRENCY_ALIASES[upper]) return CURRENCY_ALIASES[upper]
  if (CURRENCY_ALIASES[text]) return CURRENCY_ALIASES[text]
  return /^[A-Z]{3}$/.test(upper) ? upper : null
}

/** Format an amount without ever throwing.
 *
 *  Intl.NumberFormat throws RangeError on anything that is not a valid ISO 4217
 *  code, and this value comes straight from a vision model reading a document -
 *  a '₹' in the currency field is normal model output, not a bug. Left
 *  unguarded it takes down the whole React tree, which is exactly what happened.
 */
export function money(
  value: string | number | null | undefined,
  currency: string | null = 'INR',
) {
  if (value === null || value === undefined || value === '') return '—'

  let n: number
  if (typeof value === 'number') {
    n = value
  } else {
    const digits = String(value).replace(/[^0-9.-]/g, '')
    // Stripping a non-numeric string leaves '', and Number('') is 0 - which
    // would render a garbled reading as a confident 0.00. Show it verbatim
    // instead so the reviewer sees what the model actually returned.
    if (digits === '' || digits === '-' || digits === '.') return String(value)
    n = Number(digits)
  }
  if (!Number.isFinite(n)) return String(value)

  const code = normalizeCurrency(currency)
  if (code) {
    try {
      return new Intl.NumberFormat('en-IN', {
        style: 'currency', currency: code, maximumFractionDigits: 2,
      }).format(n)
    } catch {
      // Well-formed but unknown code - fall through to plain digits.
    }
  }

  const plain = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2 }).format(n)
  const label = (currency ?? '').trim()
  return label ? `${label} ${plain}` : plain
}

export function bytes(n: number) {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`
  return `${(n / 1024 / 1024).toFixed(1)} MB`
}

export function ago(iso: string) {
  const seconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000)
  if (seconds < 60) return 'just now'
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`
  return `${Math.floor(seconds / 86400)}d ago`
}

/** 'items[2].hsn_sac' -> 'Row 3 · HSN / SAC' */
export function humanPath(path: string) {
  const rowMatch = path.match(/^items\[(\d+)\]\.(.+)$/)
  if (rowMatch) return `Row ${Number(rowMatch[1]) + 1} · ${label(rowMatch[2])}`
  const parts = path.split('.')
  return parts.map(label).join(' · ')
}

export function label(key: string) {
  const special: Record<string, string> = {
    gstin: 'GSTIN', hsn_sac: 'HSN / SAC', irn: 'IRN', ifsc: 'IFSC',
    upi_id: 'UPI ID', cgst_percent: 'CGST %', sgst_percent: 'SGST %',
    igst_percent: 'IGST %', cgst_amount: 'CGST', sgst_amount: 'SGST',
    igst_amount: 'IGST', cgst_total: 'CGST total', sgst_total: 'SGST total',
    igst_total: 'IGST total', eway_bill: 'E-way bill', ack_number: 'Ack number',
    po_number: 'PO number', place_of_supply: 'Place of supply',
  }
  if (special[key]) return special[key]
  return key.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase())
}

export const STATUS_PILL: Record<string, { className: string; text: string }> = {
  received:     { className: 'pill pill-mute',   text: 'Queued' },
  extracting:   { className: 'pill pill-accent', text: 'Reading' },
  needs_review: { className: 'pill pill-warn',   text: 'Needs review' },
  approved:     { className: 'pill pill-ok',     text: 'Approved' },
  synced:       { className: 'pill pill-ok',     text: 'In Odoo' },
  rejected:     { className: 'pill pill-mute',   text: 'Rejected' },
  failed:       { className: 'pill pill-crit',   text: 'Failed' },
}
